/**
 * 状態チャンネル（索引 Issue #498 の段階 9。設計文書 §5.5）。**純関数**。
 *
 * 帯（clip）は「いつからいつまで出るか」を持つ。**状態は「今どうなっているか」だけ**を持ち、
 * アンカーの時刻で畳んでフレームごとに引く。**可変尺で出しっぱなしになるもの**
 * （チェーン・対象・処理中、そして LP・ターン・フェイズ・盤面）は帯より状態のほうが素直:
 *
 * - 尺を人が動かす意味がない（終わりは後ろのイベントが決める）
 * - タイムラインのレーンを可変長の帯が埋めてしまう
 * - 描くときに知りたいのは「今のチェーンは何段目か」であって「帯の何 ms 目か」ではない
 *
 * 3 つの作り方がある:
 *
 * | kind | 何を畳むか | 使うもの |
 * |---|---|---|
 * | `fold` | アンカーごとに `fold(prev, anchor)` で進める | LP・ターン・フェイズ |
 * | `bands` | その型の**帯のうち、その時刻に出ているもの** | チェーン・対象・処理中（**演出ファイルは無変更**） |
 * | `build` | 自前で組んで自前で引く（逃げ道） | 盤面（`board_steps` を畳む既存の実装をそのまま使う） |
 */

import { isStateLayer } from './types';

const STATE_KINDS = ['fold', 'bands', 'build'];

/** その時刻に出ている帯（`frame.js` の itemsAt と同じ。ここで持つのは states → frame の向きを保つため） */
const activeAt = (ms, objects = []) => objects
  .filter((o) => !o.hidden && o.startMs <= ms && ms < o.endMs)
  .map((o) => ({ ...o, localMs: ms - o.startMs, remainingMs: o.endMs - ms }));

const isFn = (v) => typeof v === 'function';

/** 状態チャンネルの宣言を検めて既定を埋める */
export function defineChannel(channel, where = 'channel') {
  if (!channel || typeof channel !== 'object') throw new Error(`${where}: 状態チャンネルの宣言ではありません`);
  const { id } = channel;
  if (!/^[a-z0-9_]{1,40}$/.test(id ?? '')) throw new Error(`${where}: id は英小文字・数字・_（${id}）`);
  const kind = channel.kind ?? (isFn(channel.build) ? 'build' : (isFn(channel.fold) ? 'fold' : 'bands'));
  if (!STATE_KINDS.includes(kind)) throw new Error(`${where}(${id}): kind は ${STATE_KINDS.join(' / ')}`);
  if (kind === 'fold' && !isFn(channel.fold)) throw new Error(`${where}(${id}): fold が要ります`);
  if (kind === 'build' && !isFn(channel.build)) throw new Error(`${where}(${id}): build が要ります`);
  return { initial: null, label: id, ...channel, kind };
}

/**
 * チャンネルを畳む。
 * @param anchors 整列後の行（`align.resolveAnchors` の rows）
 * @param objects 整列後のオブジェクト（`bands` と `build` のチャンネルが使う）
 * @returns `{ [id]: { channel, steps?, built?, objects? } }`
 */
export function buildStates(channels = [], { anchors = [], objects = [], ctx = {} } = {}) {
  const out = {};
  for (const channel of channels) {
    if (channel.kind === 'build') {
      /*
       * **整列後の帯も渡す**（Issue #534）。`bands` だけが使っていたが、
       * 「帯が置いたものの生存」を畳むチャンネル（演出のオブジェクト）は帯から出るので、
       * `build` 側にも要る。`anchors` の刻みでは表せない（帯の途中で置かれるものがある）。
       */
      out[channel.id] = { channel, built: channel.build(anchors, ctx, objects) };
    } else if (channel.kind === 'fold') {
      const steps = [];
      let prev = channel.initial;
      steps.push({ ms: anchors[0]?.ms ?? 0, value: prev });
      for (const anchor of anchors) {
        const next = channel.fold(prev, anchor, ctx);
        if (next === prev) continue;
        prev = next;
        // 同じ時刻に何度も変わったら最後のものだけ残す（アンカーは同じ ms に並びうる）
        if (steps.length && steps[steps.length - 1].ms === anchor.ms) steps[steps.length - 1].value = next;
        else steps.push({ ms: anchor.ms, value: next });
      }
      out[channel.id] = { channel, steps };
    } else {
      out[channel.id] = { channel, objects: objects.filter((o) => o.type === channel.id && !o.hidden) };
    }
  }
  return out;
}

/**
 * **状態を進める時刻で行の時刻を差し替える**（Issue #566）。**純関数**。
 *
 * 状態（盤面など）は「**そのアンカーの帯の頭**でそのアンカーの後の状態に進む」のが既定。帯が `commitMs`
 * （帯の頭から何 ms で進めるか。`generate.js`）を宣言していれば、そのアンカーの行の時刻を
 * **`帯の頭 + commitMs`**（帯の尺を超えれば帯の終わり）に差し替える。これを畳む側に渡すと、
 * その時刻までは状態が**アンカーの前のまま**になる（帯はその間、前の状態を借りて動かせる）。
 *
 * **宣言が無くても帯の頭に揃える**（Issue #583）。`sequential` の整列はアンカーの時刻を**フレームに丸める**が、
 * 帯の頭は前の帯の終わり（丸めない）から決まるので、**帯の頭はアンカーの時刻より最大で半フレーム遅い**
 * （丸めが切り下げたとき。前の帯と重なる同じアンカーの 2 本目以降ならもっと遅い）。アンカーの時刻のまま
 * 盤面を進めると、**帯が始まる前のフレームだけ盤面が後の状態**になり、盤のカードを借りる演出（`tweaks`）が
 * 効く前の 1 フレームに後の状態のカードが見えていた。そこで帯のあるアンカーは**いちばん早い帯の頭**まで待つ
 * （帯が始まるのと同じフレームで盤面が進む。`frame.js` の itemsAt の `startMs <= ms` と `boardAt` の `ms <= frameMs` が同じ境界）。
 *
 * - 同じアンカーの帯が複数宣言していれば遅いほう。隠した帯は数えない
 * - 宣言が無いアンカーは、**整列に関わる帯**（隠していない・常時表示でない）のうち**いちばん早い帯の頭**
 * - アンカーの時刻より前には進めない（`max(行の時刻, 宣言)`）
 * - **差し替える行が 1 つも無ければ `rows` をそのまま返す**（同一の配列。`fixed` の整列では帯の頭＝アンカーの時刻なので何も変わらない）
 * - **単調にはしない** —— 「前のアンカーが進んだ時刻より前には進まない」（`max(自分, 前)`）は、
 *   状態を畳む側が並びどおりに畳むときに付ける（盤面は `editorPorts/boardTimeline.js::buildBoardTimeline`
 *   が既にそうしている）。ここで行ぜんぶを単調にすると、状態を持たない行（ヒントなど）の時刻まで効いてしまう
 *
 * @param rows 整列後の行（`align.resolveAnchors` の rows）
 * @param objects 整列後のオブジェクト（`startMs` / `endMs` / `commitMs` / `source.anchorKey`）
 */
export function commitRows(rows = [], objects = []) {
  const declared = new Map();
  const heads = new Map();
  for (const o of objects ?? []) {
    const key = o?.source?.anchorKey;
    if (!key || o.hidden || !Number.isFinite(o.startMs)) continue;
    if (Number.isFinite(o.commitMs)) {
      const span = Number.isFinite(o.endMs) ? Math.max(0, o.endMs - o.startMs) : Infinity;
      const at = o.startMs + Math.min(Math.max(0, o.commitMs), span);
      declared.set(key, Math.max(declared.get(key) ?? -Infinity, at));
    } else if (!isStateLayer(o.layer)) {
      // 宣言の無い帯は帯の頭（Issue #583）。同じアンカーに何本あっても**いちばん早い頭**
      heads.set(key, Math.min(heads.get(key) ?? Infinity, o.startMs));
    }
  }
  if (!declared.size && !heads.size) return rows;
  let changed = false;
  const out = rows.map((r) => {
    const key = r?.key;
    const at = key ? (declared.get(key) ?? heads.get(key)) : undefined;
    if (at == null || !(at > r.ms)) return r;
    changed = true;
    return { ...r, ms: at };
  });
  return changed ? out : rows;
}

/** その時刻の値 */
function stateAt(ms, entry) {
  if (!entry) return null;
  const { channel } = entry;
  if (channel.kind === 'build') return channel.at ? channel.at(ms, entry.built) : entry.built;
  if (channel.kind === 'bands') return activeAt(ms, entry.objects);
  let value = channel.initial;
  for (const step of entry.steps ?? []) {
    if (step.ms > ms) break;
    value = step.value;
  }
  return value;
}

/** すべてのチャンネルのその時刻の値 */
export function statesAt(ms, states = {}) {
  const out = {};
  for (const [id, entry] of Object.entries(states)) out[id] = stateAt(ms, entry);
  return out;
}
